/**
 * Landing page translations: landing, cookieConsent, abuse, kubernetes,
 * vserver, productsOverview, objectStorage, emailHosting, webhosting,
 * comingSoon, pricingCalculator
 *
 * Legal documents live in ./legal.ts
 */

import { VSERVER_SMTP_BLOCK_DATE } from '@/legal/vserverSmtpBlockDate'

export const landing = {
  hero: {
    eyebrow: 'Domains · E-Mail · VServer · Webhosting',
    titleGradient: 'Domain, E-Mail, Server.',
    titleSuffix: 'Gehostet in Deutschland.',
    subtitle:
      'Alles, was dein Unternehmen online braucht, von einem Anbieter: deine Domain, E-Mail mit eigener Adresse für 1,99 € im Monat und VServer ab 7,99 €. Transparente Preise, keine versteckten Gebühren, persönlicher Support auf Deutsch.',
    ctaPrimary: 'Wunschdomain prüfen',
    ctaSecondary: 'Preise ansehen',
    ctaNote: 'Alle Preise inkl. MwSt. · Keine versteckten Gebühren',
    chips: {
      germanServers: 'Server in Deutschland',
      support: 'Persönlicher Support',
      transparentPrices: 'Transparente Preise',
      hourlyVserver: 'VServer stündlich abgerechnet'
    }
  },
  bundle: {
    title: 'Domain, E-Mail und VServer aus einer Hand',
    subtitle: 'Starte mit deiner Domain und buche dazu, was du brauchst. Jedes Produkt einzeln, ohne Pakete, die du nicht nutzt.',
    domain: {
      title: 'Domain',
      priceFrom: '.de ab {price}/Jahr',
      description: 'Sichere dir deinen Namen im Netz. Den Preis jeder Endung siehst du vor dem Kauf.',
      features: {
        dns: 'DNS-Verwaltung im Kundenportal',
        dynDns: 'DynDNS inklusive',
        perTld: 'Klare Preise pro Endung'
      },
      cta: 'Domain prüfen'
    },
    email: {
      title: 'E-Mail mit eigener Domain',
      priceFrom: '1,99 € pro Domain und Monat',
      description: 'Professionelle Adressen auf deiner Domain, gehostet in Deutschland.',
      features: {
        mailboxes: 'Bis zu 1.000 Postfächer, 10 GB geteilter Speicher',
        outbound: '1.000 ausgehende E-Mails pro Monat inklusive',
        protocols: 'Webmail, IMAP, SMTP und JMAP',
        teams: 'Aliasse, Verteiler und Delegation'
      },
      note: 'Mehr Speicher oder Versand rechnen wir transparent nach Verbrauch ab.',
      cta: 'E-Mail einrichten'
    },
    vserver: {
      title: 'VServer',
      priceFrom: 'ab 7,99 € pro Monat',
      description: 'Dein eigener Server mit Root-Zugriff, für Website, Anwendungen oder interne Tools.',
      features: {
        billing: 'Stündlich abgerechnet, höchstens der Monatspreis',
        term: 'Keine Mindestlaufzeit',
        network: 'IPv4 und IPv6 inklusive',
        tools: '2 bis 5 Snapshots je nach Tarif, Web-Konsole'
      },
      cta: 'Tarife ansehen'
    },
    webhostingHint: 'Du brauchst nur eine PHP-Website? Unser Webhosting bringt MariaDB und SFTP mit.',
    webhostingLink: 'Zum Webhosting'
  },
  k8sTeaser: {
    eyebrow: 'Für Entwickler',
    title: 'Kubernetes-Namespaces statt eigenem Cluster',
    text: 'Du deployst Container? Hol dir einen eigenen Kubernetes-Namespace mit kubectl- und Helm-Zugriff, ohne eine Control Plane zu betreiben. Feste Tarife oder Pay-as-you-go, mit kostenlosem Einstiegstarif.',
    cta: 'Kubernetes-Namespaces ansehen'
  },
  nav: {
    products: 'Produkte'
  },
  features: {
    support: 'Persönlicher Support',
    dailyBackups: 'Tägliche Backups',
    monitoring247: '24/7 Monitoring',
    dnsIncluded: 'DNS inklusive',
    whoisProtection: 'WHOIS-Schutz optional',
    autoRenewal: 'Auto-Verlängerung',
    instantRegistration: 'Sofort-Registrierung',
    dnsManagement: 'DNS-Verwaltung',
    dynDns: 'DynDNS inklusive',
    tlds500: '500+ TLDs',
    haControlPlane: 'HA Control Plane'
  },
  products: {
    domains: {
      title: 'Domains',
      tlds500: '500+ TLDs',
      findYourDomain: 'Sichere dir deine Domain',
      searchPlaceholder: 'deine-wunschdomain',
      allTlds: 'Alle TLDs'
    }
  },
  footer: {
    tagline:
      'Cloud Infrastruktur, die mitwächst. Kubernetes, VServer, Webhosting, Mail und Domains — aus einer Hand.',
    products: 'Produkte',
    allProducts: 'Alle Produkte',
    pricingCalculator: 'Preisrechner',
    tldOverview: 'Alle Domain-Endungen',
    support: 'Support',
    legal: 'Rechtliches',
    knowledgeBase: 'Knowledge-Base',
    status: 'Status',
    docs: 'Dokumentation',
    contact: 'Kontakt',
    imprint: 'Impressum',
    privacy: 'Datenschutz',
    terms: 'AGB',
    nis2: 'NIS2-Richtlinien',
    sla: 'SLA',
    withdrawal: 'Widerrufsbelehrung',
    cancellation: 'Verträge hier kündigen',
    reportAbuse: 'Missbrauch melden',
    cookieSettings: 'Cookie-Einstellungen',
    copyright: '© {year} ITSH. Alle Rechte vorbehalten.',
    pricesIncludeTax: 'Preise inkl. MwSt.'
  }
} as const

export const cookieConsent = {
  text: 'Wir verwenden Cookies, um unsere Werbemaßnahmen zu optimieren. ',
  learnMore: 'Mehr erfahren',
  declineBtn: 'Nur notwendige',
  acceptBtn: 'Alle akzeptieren',
  ariaLabel: 'Cookie-Einwilligung'
} as const

export const abuse = {
  title: 'Missbrauch melden',
  subtitle: 'Melden Sie uns verdächtige Aktivitäten oder Verstöße auf von uns betriebenen Domains, einschließlich Kundendomains, itsh.dev und itsh-apps.dev, oder von unseren Servern ausgehende Aktivitäten wie Spam.',
  categories: {
    spam: 'Spam',
    phishing: 'Phishing',
    malware: 'Malware',
    fakeData: 'Falsche Kontaktdaten',
    trademark: 'Markenrechtsverletzung',
    other: 'Sonstiges'
  },
  form: {
    email: 'Ihre E-Mail-Adresse',
    emailPlaceholder: "name{'@'}beispiel.de",
    name: 'Ihr Name',
    nameOptional: 'Ihr Name (bei „Sonstiges" optional)',
    namePlaceholder: 'Max Mustermann',
    target: 'Was möchten Sie melden?',
    targetDomain: 'Eine Domain',
    targetIp: 'Eine IP-Adresse',
    domain: 'Betroffene Domain',
    ip: 'Betroffene IP-Adresse',
    ipInvalid: 'Bitte geben Sie eine gültige IPv4- oder IPv6-Adresse an.',
    url: 'Genauer Ort des Inhalts (URL)',
    urlOptional: 'Genauer Ort des Inhalts (URL, optional)',
    urlHint: 'Bitte die vollständige Adresse der beanstandeten Seite angeben, nicht nur die Domain.',
    goodFaith: 'Ich versichere nach bestem Wissen und Gewissen, dass meine Angaben richtig und vollständig sind.',
    category: 'Art des Missbrauchs',
    categoryPlaceholder: 'Bitte auswählen',
    description: 'Beschreibung',
    descriptionPlaceholder: 'Bitte beschreiben Sie den Vorfall so detailliert wie möglich...',
    descriptionMinLength: 'Mindestens 10 Zeichen erforderlich',
    submit: 'Meldung absenden'
  },
  success: {
    title: 'Meldung eingereicht',
    message: 'Vielen Dank für Ihre Meldung. Unser Team wird den Fall prüfen und bei Bedarf Maßnahmen ergreifen. Über das Ergebnis informieren wir Sie unter Angabe der Gründe.',
    outOfScope: 'Die gemeldete Domain wird nicht von uns betrieben. Wir haben Ihre Meldung erfasst, sind hierfür aber nicht der richtige Adressat — bitte wenden Sie sich an den zuständigen Anbieter.',
    outOfScopeIp: 'Die gemeldete IP-Adresse gehört nicht zu unseren Servern. Wir haben Ihre Meldung erfasst, sind hierfür aber nicht der richtige Adressat. Bitte wenden Sie sich an den zuständigen Anbieter.',
    caseId: 'Referenznummer: {id}',
    close: 'Schließen'
  },
  captchaError: 'Captcha-Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
  privacyNotice: 'Ihre Daten werden gemäß unserer Datenschutzerklärung verarbeitet und nur zur Bearbeitung der Meldung verwendet.',
  tooManyReports: 'Zu viele Meldungen. Bitte warten Sie einen Moment.'
} as const

export const kubernetes = {
  title: 'Kubernetes pro Namespace —',
  titleSuffix: 'ohne eigenen Cluster',
  eyebrow: 'Managed Kubernetes aus Deutschland — pro Namespace',
  subtitle:
    'Du bekommst einen echten Kubernetes-Namespace mit vollem kubectl-, Helm- und CI/CD-Zugriff, ganz ohne Control Plane, die du betreiben, patchen oder bezahlen musst. Wir betreiben den Cluster, du deployst. Kostenlos starten.',
  cta: 'Kostenlos starten',
  ctaSecondary: 'Preise ansehen',
  chips: {
    germanDatacenters: 'Deutsche Rechenzentren',
    cheaperThanEks: 'Günstiger als AWS EKS'
  },
  features: {
    title: 'Managed Kubernetes ohne Ops-Aufwand',
    security: {
      title: 'Sicherheit & Backups',
      description: 'Netzwerkisolation, strikte Quotas und tägliche Backups.'
    },
    transparentPricing: {
      title: 'Transparente Preise',
      description: 'Basisplan + gemessene Ressourcen-Reservierung. Keine Überraschungen.'
    },
    managed: {
      title: 'Wir managen es für dich',
      description: 'Upgrades, Patches und 24/7 Monitoring.'
    },
    haControlPlane: {
      title: 'Hochverfügbare Control Plane',
      description: 'Redundante Control Plane mit automatischem Failover für maximale Verfügbarkeit.'
    }
  },
  pricing: {
    fixedTiersLabel: 'Feste Tarife',
    title: 'Namespace-Preise',
    subtitle: 'Transparenter Basispreis + gemessene Ressourcen-Reservierung.',
    clusterTeaser: 'Dedicated Clusters ab €399/Monat.',
    clusterContact: 'Kontakt aufnehmen',
    intervals: {
      month: 'Monat'
    },
    popular: 'Beliebt',
    recommended: 'Empfohlen',
    startNow: 'Jetzt starten',
    ownControlPlane: 'Eigene Control Plane',
    euDataResidency: 'EU-Datenresidenz',
    addons: 'Add-ons',
    egressIncluded: '{amount} GiB Egress inklusive',
    sharedFeatures: 'Alle Pläne inkl. tägliche Backups, 24/7 Monitoring und deutsche Rechenzentren.',
    freeGuidance: 'Zum Ausprobieren — dauerhaft kostenlos',
    paygGuidance: 'Für schwankende Workloads',
    fixedGuidance: 'Für planbare Workloads'
  },
  payg: {
    subtitle: 'Abrechnung nach CPU- & RAM-Nutzung, solange deine Pods laufen',
    billingNote: 'Die Abrechnung erfolgt nach dem höheren Wert aus reservierter (angeforderter) und tatsächlich genutzter CPU & RAM. Speicher wird nach bereitgestellter Kapazität abgerechnet. Egress wird nach tatsächlichem Traffic abgerechnet.',
    baseFee: 'Monatliche Grundgebühr',
    rateInfo: 'vCPU €0,015/Core-Stunde · RAM €0,005/GiB-Stunde · Storage €0,08/GiB-Monat · Egress €0,01/GiB',
    rates: {
      cpuUnit: '/Core-Stunde',
      memoryUnit: '/GiB-Stunde',
      storageUnit: '/GiB-Monat',
      egressUnit: '/GiB'
    },
    features: {
      noMinimum: 'Kein Mindestverbrauch',
      spendingCap: 'Budget-Benachrichtigungen konfigurierbar'
    }
  },
  costCompare: {
    title: 'Warum nicht einfach AWS?',
    subtitle: 'Du brauchst keinen Cluster. Du brauchst einen Namespace.',
    itshLabel: 'ITSH Namespace',
    awsLabel: 'AWS EKS',
    selfHostedLabel: 'Self-Hosted',
    monthlyCost: 'Monatliche Kosten',
    itshCost: 'ab €3/Monat',
    awsCost: 'ab $73/Monat',
    selfHostedCost: 'ab €150/Monat',
    setupTime: 'Setup-Zeit',
    itshSetup: '2 Minuten',
    awsSetup: '30–60 Minuten',
    selfHostedSetup: '2–5 Tage',
    opsOverhead: 'Ops-Aufwand',
    itshOps: 'Keiner — wir managen es',
    awsOps: 'Mittel — Updates, Netzwerk, IAM',
    selfHostedOps: 'Hoch — alles selbst',
    controlPlaneNote: 'AWS EKS berechnet $73/Monat allein für die Control Plane — bevor ein einziger Container läuft.',
    savingsHighlight: 'günstiger als die AWS-EKS-Control-Plane',
    sourceLabel: 'Quellen',
    sourceTooltip: 'AWS EKS: aws.amazon.com/eks/pricing, Stand März 2026. Der Wert von $73/Monat gilt für EKS Standard-Support. Self-Hosted-Schätzungen basieren auf einem 3-Node-Setup mit dedizierten Servern. Setup-Zeiten sind Erfahrungswerte.'
  },
  faq: {
    title: 'Häufige Fragen',
    items: {
      q1: 'Was ist der Unterschied zwischen Namespace und Cluster?',
      a1: 'Ein Namespace ist ein isolierter Bereich innerhalb eines geteilten Clusters. Du bekommst eigene CPU-, RAM- und Storage-Quotas, Network Policies und RBAC — ohne einen eigenen Cluster verwalten zu müssen. Ideal für kleine bis mittlere Teams.',
      q2: 'Wie viel kostet Managed Kubernetes im Vergleich zu AWS?',
      a2: 'AWS EKS berechnet $73/Monat nur für die Control Plane, plus Compute-Kosten. Bei ITSH kannst du zunächst kostenlos im Free Tier starten und später in einen PAYG-Namespace ab €3/Monat Grundgebühr mit nutzungsbasierter CPU- und RAM-Abrechnung wechseln. Das unterbietet die AWS-EKS-Control-Plane, ohne Vorabverpflichtung.',
      q3: 'Brauche ich ein DevOps-Team für Kubernetes?',
      a3: 'Nein. Wir kümmern uns um Updates, Sicherheitspatches, Monitoring und Backups. Du deployst deine Apps via kubectl, Helm oder CI/CD — den Rest übernehmen wir.',
      q4: 'Wie migriere ich meine App zu Kubernetes?',
      a4: 'Containerisiere deine App mit einem Dockerfile, schreibe ein Kubernetes-Manifest oder Helm Chart, und deploye es in deinen Namespace. Unser Support hilft dir gerne beim Einstieg.',
      q5: 'Wo stehen die Server?',
      a5: 'Unsere Kubernetes-Cluster laufen in deutschen Rechenzentren. Den Auftragsverarbeitungsvertrag (AVV) nach Art. 28 DSGVO schließt du direkt im Kundenbereich ab.',
      q6: 'Kann ich von AWS/GCP zu ITSH wechseln?',
      a6: 'Ja. Kubernetes-Workloads sind portabel. Exportiere deine Manifeste, passe die Container-Registry und Ingress-Konfiguration an, und deploye sie in deinen ITSH-Namespace. Wir unterstützen Standard-Kubernetes — kein Vendor Lock-in.',
      q7: 'Gibt es einen Free Tier?',
      a7: 'Ja. Jedes Kundenkonto kann einmalig einen dauerhaft kostenlosen Kubernetes-Namespace beanspruchen: 200 mCPU, 512 MiB RAM, 1 GiB NFS-Storage und 10 GiB ausgehender Traffic pro Monat. Du bestätigst deine Telefonnummer; schlagen Risikosignale an, kann zusätzlich eine Kartenbestätigung über 0 € nötig sein. Verfügbar in EU/EEA, Großbritannien, USA, Kanada, Australien, Neuseeland und der Schweiz. Mit einem Klick auf einen kostenpflichtigen Tarif upgradebar; deine Workloads laufen während des Wechsels ohne Unterbrechung weiter.'
    }
  }
} as const

export const vserver = {
  title: 'VServer',
  titleSuffix: 'alles inklusive',
  eyebrow: 'VPS — Virtuelle Server',
  subtitle:
    'VPS auf dedizierter Hardware. Snapshots, Traffic und direkter Support sind schon im Preis. Keine Add-ons, keine versteckten Gebühren.',
  cta: 'Tarife ansehen — ab {price}',
  heroPriceline: 'Ab {price} pro Monat: {vcpu} vCPU, {ram} GB RAM, {disk} GB NVMe. Stündlich abgerechnet, höchstens zum Monatspreis.',
  heroChips: {
    nvme: 'NVMe SSDs',
    snapshots: 'Snapshots inklusive',
    traffic: 'Traffic inklusive',
    support: 'Persönlicher Support'
  },
  features: {
    title: 'Was VServer von ITSH besonders macht',
    nvme: {
      title: 'Dedizierter NVMe-Speicher',
      description: 'Enterprise-NVMe direkt im Server — kein Shared Cloud-Storage, kein Throttling.'
    },
    conservative: {
      title: 'Verlässliche CPU-Leistung',
      description:
        'Wir halten auf jedem Node bewusst Leistung in Reserve — deine vCPUs bleiben auch unter Last reaktionsschnell. Keine Server am Anschlag, kein Drosseln.'
    },
    snapshots: {
      title: 'Snapshots inklusive',
      description: '2 bis 5 Snapshots pro Server je nach Tarif, jederzeit zurückspielbar. Kein Aufpreis pro GB.'
    },
    ipv6: {
      title: 'IPv6 /112 pro VM',
      description: '65.536 IPv6-Adressen für jeden VServer. Auch für Container, Docker und Microservices.'
    },
    firewall: {
      title: 'Firewall-Regeln im Panel',
      description: 'Lege eigene Regeln pro Server im Panel an, angewendet vor der VM. Solange du keine anlegst, ist aller Verkehr erlaubt, bis auf ausgehenden Port 25 in den ersten drei Monaten (siehe Häufige Fragen).'
    },
    support: {
      title: 'Direkter Support',
      description: 'Kleines Team, schnelle Antworten. Kein Ticket-Wartezimmer.'
    }
  },
  pricing: {
    title: 'Preise',
    subtitle: 'Alle Preise inkl. 19% MwSt. Bei der Bestellung zahlst du den ersten Monat, danach wird stündlich abgerechnet, höchstens bis zum Monatspreis. Keine Mindestlaufzeit. Snapshots und IPv6 /112 in jedem Tarif inklusive.',
    perMonth: '/Monat',
    popular: 'Beliebt',
    startNow: 'Jetzt bestellen',
    compareLink: 'Du vergleichst VServer-Anbieter? Zum VServer-Vergleich',
    unavailable: 'Dieser Tarif ist derzeit nicht verfügbar. Bitte versuche es später erneut.',
    sharedFeatures: 'Alle Tarife inkl. Traffic, Snapshots, IPv6 /112 und persönlichen Support.',
    specs: {
      vcpu: '{count} vCPU',
      vcpus: '{count} vCPUs',
      ram: '{amount} GB RAM',
      disk: '{amount} GB NVMe',
      snapshots: '{count} Snapshot inkl. | {count} Snapshots inkl.',
      ipv4: '1 IPv4-Adresse',
      ipv6: 'IPv6 /112'
    },
    tiers: {
      s: 'VServer S',
      m: 'VServer M',
      l: 'VServer L',
      xl: 'VServer XL'
    }
  },
  faq: {
    title: 'Häufige Fragen',
    items: {
      q1: 'Worauf laufen die VServer?',
      a1: 'Auf dedizierter Hardware in einem EU-Rechenzentrum — mit ECC-RAM und Enterprise-NVMe-SSDs.',
      q2: 'Sind Backups inklusive?',
      a2: 'Nein, ein VServer enthält keine Backups. Du kannst je nach Tarif 2 bis 5 Snapshots pro Server anlegen und jederzeit zurückspielen. Ein Snapshot liegt auf demselben Speicher wie der Server und ersetzt kein Backup: Daten, die du nicht verlieren darfst, sicherst du selbst an einem anderen Ort.',
      q3: 'Kann ich meinen Tarif später ändern?',
      a3: 'Ja. Upgrade und Downgrade jederzeit möglich. Die Festplatte kann dabei nur wachsen, nicht schrumpfen. Ab dem Wechsel gilt der Stundensatz des neuen Tarifs, und die Obergrenze für den Monat richtet sich nach dem aktuellen Tarif.',
      q4: 'Wie funktioniert die Web-Konsole?',
      a4: 'Direkt im Panel über eine sichere Web-Konsole — kein separater VNC-Client nötig.',
      q5: 'Kann ich das Betriebssystem neu installieren?',
      a5: 'Ja. Per Klick installierst du das Betriebssystem mit einer unserer Vorlagen komplett neu. Einen Rescue-Modus gibt es derzeit nicht. Geht SSH nicht mehr, erreichst du den Server über die Web-Konsole im Kundenportal, auch wenn sein Netzwerk nicht funktioniert.',
      q6: 'Welche Betriebssysteme bietet ihr an?',
      a6: 'Debian, Ubuntu, Rocky, Alpine, Arch, Fedora, openSUSE und Flatcar, jeweils in einer aktuellen Version.',
      q7: 'Ist Traffic inklusive?',
      a7: 'Ja. Ein- und ausgehender Traffic ist im Preis enthalten — ohne Volumenpakete oder Aufpreise. Es gilt Fair Use: normale Server-Workloads erreichen keine Grenze.',
      q8: 'Kann ich einen eigenen Mailserver betreiben?',
      a8: `Ja. In den ersten drei Monaten nach deiner ersten vServer-Bestellung ist der ausgehende Port 25 (Zustellung an fremde Mailserver) gesperrt, um Spam von frischen Servern zu verhindern. Mails über die Submission-Ports 587 und 465 an deinen Mailanbieter gehen jederzeit. Brauchst du Port 25 früher, beantrage die Freigabe mit kurzer Begründung im Kundenportal. Wer vor dem ${VSERVER_SMTP_BLOCK_DATE.de} schon einen vServer hatte, ist nicht betroffen.`
    }
  }
} as const

export const vserverCompare = {
  eyebrow: 'Für alle, die VServer-Anbieter vergleichen',
  title: 'VServer-Vergleich: worauf es wirklich ankommt',
  intro:
    'Beim VServer zählt mehr als der Preis pro vCPU. Laufzeit, Standort, Abrechnung und wer dir antwortet, wenn etwas klemmt, entscheiden, ob ein Server zu deinem Projekt passt. Hier siehst du, was ITSH in jedem Tarif liefert, und auch, was noch fehlt.',
  tiers: {
    title: 'Die Tarife',
    headers: {
      tier: 'Tarif',
      vcpu: 'vCPU',
      ram: 'RAM',
      disk: 'Speicher',
      snapshots: 'Snapshots',
      price: 'Höchstens pro Monat'
    },
    note: 'Preise inkl. 19 % MwSt. Der erste Monat wird bei der Bestellung bezahlt, danach rechnen wir stündlich ab, höchstens bis zum Monatspreis.'
  },
  criteria: {
    title: 'Was du vergleichen solltest',
    headers: {
      criterion: 'Kriterium',
      check: 'Worauf du achten solltest',
      itsh: 'Bei ITSH, in jedem Tarif'
    },
    rows: {
      term: {
        criterion: 'Laufzeit',
        check: 'Gibt es eine Mindestlaufzeit, die dich bindet?',
        itsh: 'Keine Mindestlaufzeit.'
      },
      billing: {
        criterion: 'Abrechnung',
        check: 'Zahlst du ganze Monate oder die tatsächliche Nutzung?',
        itsh: 'Erster Monat bei Bestellung, danach stündlich, höchstens der Monatspreis.'
      },
      location: {
        criterion: 'Standort',
        check: 'In welchem Land stehen die Server, wer betreibt sie?',
        itsh: 'Rechenzentrum in Deutschland, betrieben von einem deutschen Unternehmen.'
      },
      network: {
        criterion: 'IPv4 und IPv6',
        check: 'Ist eine IPv4-Adresse dabei, gibt es IPv6?',
        itsh: '1 IPv4-Adresse und ein IPv6-Netz (/112) pro Server.'
      },
      snapshots: {
        criterion: 'Snapshots',
        check: 'Kannst du den Stand vor einem Update sichern?',
        itsh: '2 bis 5 Snapshots pro Server je nach Tarif, siehe Tariftabelle.'
      },
      backups: {
        criterion: 'Backups',
        check: 'Werden deine Daten automatisch an einem anderen Ort gesichert?',
        itsh: 'Noch nicht im Angebot. Siehe Hinweis unten.'
      },
      access: {
        criterion: 'Notfallzugang',
        check: 'Kommst du an den Server, wenn SSH nicht mehr geht?',
        itsh: 'Web-Konsole im Kundenportal.'
      },
      sizing: {
        criterion: 'Größen und Images',
        check: 'Kannst du Größen frei wählen oder eigene Images booten?',
        itsh: 'Feste Tarife S bis XL mit unseren Betriebssystem-Vorlagen.'
      },
      support: {
        criterion: 'Support',
        check: 'In welcher Sprache, und erreichst du jemanden, der das System kennt?',
        itsh: 'Per Ticket auf Deutsch oder Englisch, direkt vom Betreiber.'
      }
    }
  },
  backupNote: {
    title: 'Ehrlich gesagt: Backups gibt es noch nicht',
    text: 'Unsere VServer haben derzeit keine automatischen Backups. Du kannst je nach Tarif 2 bis 5 Snapshots pro Server anlegen, etwa vor einem Update. Ein Snapshot ersetzt aber kein Backup an einem anderen Ort. Wenn deine Daten wichtig sind, sichere sie zusätzlich extern.'
  },
  cta: {
    title: 'Dein Server in Deutschland, stündlich abgerechnet',
    subtitle: 'Wähle einen Tarif, ohne Mindestlaufzeit und ohne versteckte Kosten. Fragen vorab beantworten wir dir persönlich.',
    primary: 'Tarife ansehen',
    secondary: 'Frage stellen'
  }
} as const

export const productsOverview = {
  title: 'Eine EU-Plattform für deinen ganzen Stack.',
  subtitle:
    'Kubernetes, VServer, Webhosting, E-Mail, Object Storage und Domains: alles in einem Panel, ohne versteckte Kosten. Die Cloud-Bausteine, die du wirklich brauchst, betrieben in der EU und mit echtem Support.',
  detailsLink: 'Details & Preise',
  kubernetes: {
    description: 'Voller kubectl-Zugriff ohne eigenen Cluster — dein Namespace ist in Minuten startklar.',
    priceFrom: 'Free Tier — dann ab 3 €/Monat',
    features: {
      autoScaling: 'Autoscaling',
      loadBalancing: 'Load Balancing',
      freeTier: 'Free Tier, dauerhaft kostenlos'
    }
  },
  vserver: {
    description: 'Premium-VPS mit Root-Zugriff und NVMe-Speicher, in vier festen Größen.',
    priceFrom: 'ab 7,99 €/Monat',
    features: {
      rootAccess: 'Voller Root-Zugriff',
      nvmeStorage: 'NVMe-SSD-Speicher',
      allInclusive: 'Traffic & Snapshots inklusive'
    }
  },
  webhosting: {
    description:
      'Zuverlässiges Hosting mit SSD-Speicher, SSL-Zertifikaten und E-Mail-Postfächern.',
    priceFrom: 'ab 3,49 €/Monat',
    features: {
      sftpCron: 'SFTP & Cron-Jobs',
      freeSsl: 'SSL-Zertifikate inklusive',
      emailIncluded: 'E-Mail inklusive'
    }
  },
  domains: {
    description: 'Sichere dir deine Wunschdomain aus über 500 verfügbaren Endungen.',
    priceFrom: '.de ab {price}/Jahr',
    features: {
      tlds500: 'Über 500 TLDs',
      dnsManagement: 'DNS-Management',
      whoisProtection: 'WHOIS-Schutz optional',
      dynDns: 'DynDNS inklusive'
    }
  },
  objectStorage: {
    description: 'S3-kompatibler Objektspeicher für Backups, Assets und Anwendungsdaten.',
    priceFrom: '0,015 €/GB/Monat',
    features: {
      s3Compatible: 'S3-kompatible API',
      threeEuRegions: '3 EU-Regionen',
      payAsYouGo: 'Pay-as-you-go'
    }
  },
  emailHosting: {
    description: 'Bis zu 1.000 Postfächer auf deiner eigenen Domain.',
    priceFrom: '1,99 €/Monat pro Domain',
    features: {
      mailboxes: 'Bis zu 1.000 Postfächer',
      tenGbStorage: '{storage} GB Speicher',
      autoSecurity: 'DKIM/SPF/DMARC'
    }
  },
  decisionStrip: {
    title: 'Nicht sicher, was passt?',
    items: {
      containers: { question: 'Du deployst Container?', product: 'Kubernetes' },
      rootControl: { question: 'Du willst volle Root-Kontrolle?', product: 'VServer' },
      website: { question: 'Du willst einfach Website plus E-Mail?', product: 'Webhosting' }
    },
    calculatorPrompt: 'Oder rechne dein Setup durch:',
    calculatorLink: 'Zum Preisrechner',
    supportNote: 'Persönlicher deutschsprachiger Support inklusive.'
  }
} as const

export const objectStorage = {
  title: 'S3-Speicher ohne Rechnungsschock',
  eyebrow: 'S3-kompatibler Speicher',
  subtitle:
    'Richte aws-cli, boto3 oder jedes S3-SDK auf unseren Endpoint — ohne Migration, ohne Code-Änderung. 0,015 €/GB Speicher, pauschal 0,005 €/GB Traffic, Anfragen inklusive. Keine Einrichtungsgebühr, keine Mindestlaufzeit. Wähle die Region, in der deine Daten liegen.',
  cta: 'Jetzt starten',
  ctaNote: 'Kostenlose Registrierung — du zahlst nur, was du speicherst.',
  ctaPricing: 'Preise ansehen',
  codeProof: {
    bucket: 'dein-bucket',
    caption: 'Das ist die ganze Migration.'
  },
  heroChips: {
    s3Compatible: 'S3-kompatibel',
    regions: '3 EU-Regionen',
    gdpr: 'AVV nach Art. 28 DSGVO',
    payAsYouGo: '0,015 €/GB Speicher'
  },
  features: {
    s3Api: {
      title: 'S3-kompatible API',
      description: 'Nutze aws-cli, boto3, oder jedes S3-SDK. Keine Umstellung nötig.'
    },
    multiRegion: {
      title: 'Drei EU-Regionen',
      description: 'Nürnberg, Falkenstein und Helsinki. Daten bleiben in der EU.'
    },
    encryption: {
      title: 'Verschlüsselung',
      description: 'TLS-gesicherte Übertragung. Optionale serverseitige Verschlüsselung (SSE-C) verfügbar.'
    },
    billing: {
      title: 'Einfache Abrechnung',
      description: 'Nur zahlen, was du nutzt. Keine versteckten Kosten, keine Mindestlaufzeit.'
    },
    scalable: {
      title: 'Unbegrenzt skalierbar',
      description: 'Von Megabytes bis Petabytes — ohne Vorab-Provisionierung.'
    },
    dashboard: {
      title: 'Dashboard-Verwaltung',
      description: 'Buckets und Zugangsdaten bequem über das Control Panel verwalten.'
    }
  },
  pricing: {
    title: 'Transparente Preise',
    subtitle: 'Nur zahlen, was du nutzt. Keine Einrichtungsgebühr, keine Mindestlaufzeit.',
    storage: {
      label: 'Speicher',
      price: '0,015 €',
      unit: 'pro GB / Monat'
    },
    egress: {
      label: 'Ausgehender Traffic',
      price: '0,005 €',
      unit: 'pro GB'
    },
    requests: {
      label: 'Anfragen',
      value: 'Inklusive'
    },
    example: {
      title: 'Beispielrechnung',
      description: '100 GB Speicher + 50 GB Traffic',
      result: '1,75 € / Monat'
    },
    calculatorLink: 'Eigenes Setup durchrechnen → Preisrechner'
  },
  regions: {
    title: 'Verfügbare Regionen',
    euCentral1: {
      name: 'eu-central-1',
      location: 'Nürnberg'
    },
    euCentral2: {
      name: 'eu-central-2',
      location: 'Falkenstein'
    },
    euNorth1: {
      name: 'eu-north-1',
      location: 'Helsinki'
    }
  },
  useCases: {
    title: 'Einsatzmöglichkeiten',
    backups: {
      title: 'Backups & Archivierung',
      description: 'Automatisierte Backups deiner Anwendungen und Datenbanken sicher speichern.'
    },
    staticAssets: {
      title: 'Statische Assets',
      description: 'Bilder, Videos und Downloads für Websites und Apps bereitstellen.'
    },
    appStorage: {
      title: 'Anwendungsdaten',
      description: 'Direkte Integration in deine Anwendungen über die S3-API.'
    },
    archives: {
      title: 'Datenarchiv',
      description: 'Langzeitspeicherung von Logs, Dokumenten und Compliance-Daten.'
    }
  },
  closing: {
    title: 'Bucket anlegen, loslegen.',
    subtitle: 'Keine Einrichtungsgebühr, keine Mindestlaufzeit — wenn du nichts speicherst, zahlst du nichts.'
  }
} as const

export const emailHosting = {
  title: 'Bis zu 1.000 Postfächer. Ein fester Preis.',
  eyebrow: 'E-Mail Hosting',
  subtitle:
    '1,99 €/Monat pro Domain: bis zu 1.000 Postfächer und 5.000 Aliasse auf deiner eigenen Domain, 10 GB geteilter Speicher, schnelle JMAP-Synchronisierung, Webmail und IMAP/SMTP inklusive. Server in Deutschland, niemals für Werbung gescannt.',
  cta: 'Jetzt für 1,99 € starten',
  heroChips: {
    mailboxes: 'Bis zu 1.000 Postfächer',
    german: 'Deutsche Server',
    noAds: 'Nie für Werbung gescannt',
    dkim: 'DKIM/SPF/DMARC'
  },
  features: {
    mailboxes: {
      title: '1.000 Postfächer & 5.000 Aliasse',
      description: 'Bis zu 1.000 Postfächer und 5.000 Aliasse oder Verteiler pro Domain, ohne Aufpreis.'
    },
    autoSecurity: {
      title: 'Automatische E-Mail-Sicherheit',
      description: 'DKIM, SPF und DMARC werden automatisch konfiguriert. Deine E-Mails landen im Posteingang.'
    },
    protocols: {
      title: 'JMAP, IMAP, SMTP & Webmail',
      description: 'Nutze dein bevorzugtes E-Mail-Programm — Thunderbird, Outlook, Apple Mail — oder greife über Webmail zu.'
    },
    storage: {
      title: '{storage} GB geteilter Speicher',
      description: '{storage} GB Speicher, flexibel auf alle Postfächer verteilt. Erweiterbar für 0,10 €/GB pro Monat.'
    },
    germanInfra: {
      title: 'Deutsche Infrastruktur',
      description: 'Gehostet in deutschen Rechenzentren. Deine Daten bleiben in der EU, und deine Postfächer werden niemals für Werbung gescannt.'
    },
    mailingLists: {
      title: 'Mailinglisten & Delegation',
      description: 'Erstelle Mailinglisten für Teams oder Projekte. Delegiere Postfächer mit eigenen App-Passwörtern.'
    }
  },
  pricing: {
    title: 'Transparente Preise',
    subtitle: 'Ein Tarif. Alles inklusive. Keine versteckten Kosten.',
    base: {
      label: 'E-Mail Hosting',
      price: '1,99 €',
      unit: 'pro Domain / Monat'
    },
    included: {
      label: 'Inklusive',
      items: [
        'Bis zu 1.000 Postfächer & 5.000 Aliasse',
        '10 GB geteilter Speicher',
        '1.000 ausgehende Nachrichten/Monat — über 30 pro Tag',
        'DKIM / SPF / DMARC',
        'IMAP / SMTP / Webmail',
        'App-Passwörter',
        'Mailinglisten & Delegation'
      ]
    },
    overages: {
      title: 'Zusätzliche Nutzung',
      storage: {
        label: 'Zusätzlicher Speicher',
        price: '0,10 €',
        unit: 'pro GB / Monat'
      },
      outbound: {
        label: 'Zusätzliche Nachrichten',
        price: '1,50 €',
        unit: 'pro 1.000 Nachrichten'
      }
    },
    note: 'Monatlich kündbar. Preise inkl. MwSt.'
  },
  comparison: {
    title: 'Warum ITSH?',
    subtitle: 'Vergleiche selbst: bis zu 1.000 Postfächer zum Preis, den andere für ein einziges verlangen.',
    callout: 'ITSH ist der einzige Anbieter im Vergleich, bei dem bis zu 1.000 Postfächer pro Domain im Preis enthalten sind und die Server in Deutschland stehen. Und bei mailbox.org zahlst du pro Postfach: 5 Postfächer kosten dort 12,50 €/Monat, bei ITSH bleibst du bei 1,99 €.',
    disclaimer: 'Preise und Serverstandorte basierend auf öffentlich verfügbaren Anbieterangaben, Stand Juli 2026. Änderungen vorbehalten.',
    headers: {
      provider: 'Anbieter',
      price: 'Preis/Monat',
      mailboxes: 'Postfächer',
      storage: 'Speicher',
      customDomain: 'Eigene Domain',
      germanServers: 'Server in Deutschland'
    }
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: {
      q1: 'Kann ich meine bestehende Domain verwenden?',
      a1: 'Ja. Du kannst jede Domain nutzen, die du besitzt. Wenn du deine Domain bereits bei ITSH registriert hast, ist die Einrichtung besonders einfach. Für externe Domains musst du lediglich die MX- und TXT-Records anpassen.',
      q2: 'Wie viele Postfächer kann ich anlegen?',
      a2: "Bis zu 1.000 Postfächer und 5.000 Aliasse oder Verteiler pro Domain, etwa info{'@'}, support{'@'}, max{'@'} und team{'@'}, alles inklusive. Alle Postfächer teilen sich die 10 GB gemeinsamen Speicher.",
      q3: 'Reichen 1.000 ausgehende Nachrichten pro Monat?',
      a3: 'Für die meisten Nutzer und kleine Teams ja. Das sind über 30 E-Mails pro Tag. Wenn du mehr brauchst, zahlst du 1,50 € pro 1.000 zusätzliche Nachrichten — flexibel und ohne Tarifwechsel.',
      q4: 'Wie funktioniert DKIM/SPF/DMARC?',
      a4: 'Diese E-Mail-Sicherheitsstandards schützen deine Domain vor Spoofing und sorgen dafür, dass deine E-Mails nicht im Spam landen. Bei ITSH werden alle drei automatisch eingerichtet.',
      q5: 'Kann ich mein bestehendes E-Mail-Programm verwenden?',
      a5: 'Ja. ITSH E-Mail unterstützt JMAP, IMAP und SMTP. Du kannst Thunderbird, Outlook, Apple Mail oder jedes andere E-Mail-Programm verwenden. Für zusätzliche Apps stehen App-Passwörter zur Verfügung.',
      q6: 'Wo stehen die Server?',
      a6: 'Deine E-Mails liegen auf Servern in deutschen Rechenzentren, Sicherungskopien in der EU. ITSH ist ein deutsches Unternehmen, und den Auftragsverarbeitungsvertrag (AVV) nach Art. 28 DSGVO schließt du direkt im Kundenbereich ab.',
      q7: 'Kann ich später mehr Speicher hinzubuchen?',
      a7: 'Ja. Zusätzlicher Speicher kostet 0,10 € pro GB und Monat. Du kannst deinen Speicher jederzeit erweitern — ohne Tarifwechsel oder Wartezeit.',
      q8: 'Gelten für neue Konten besondere Versandlimits?',
      a8: 'Ja. In den ersten 30 Tagen ab Registrierung deines Kundenkontos kannst du 30 Nachrichten pro Stunde und 100 pro Tag je Postfach versenden. Das hält Spam von frisch angelegten Konten fern. Nachrichten darüber hinaus gehen nicht verloren: Sie werden vorübergehend abgewiesen und bleiben in deinem E-Mail-Programm. Je nach Programm wird die Zustellung automatisch wiederholt, sonst sendest du sie erneut. Nach 30 Tagen gelten die regulären Limits, ohne dass du etwas tun musst. Migrierst du bestehende Postfächer, kannst du die Beschränkung im Kundenportal vorzeitig aufheben lassen.'
    }
  },
  finalCta: {
    title: 'Professionelle E-Mail in 5 Minuten',
    subtitle: 'Registriere dich kostenlos und richte deine E-Mail-Adressen ein. Keine Einrichtungsgebühr, monatlich kündbar.'
  },
  domainInput: {
    placeholder: 'deinedomain.de',
    addToCart: 'In den Warenkorb',
    addedToCart: 'Zum Warenkorb hinzugefügt',
    goToCart: 'Zum Warenkorb',
    addAnother: 'Weitere Domain hinzufügen',
    noDomain: 'Du hast noch keine Domain?',
    registerOne: 'Jetzt registrieren',
    error: {
      invalid: 'Bitte gib einen gültigen Domainnamen ein',
      taken: 'Für diese Domain ist bereits E-Mail Hosting aktiv',
    }
  }
} as const

export const webhosting = {
  title: 'Webhosting',
  paymentInterval: 'Zahlungsweise',
  intervals: {
    monthly: 'Monatlich',
    yearly: 'Jahr'
  },
  intervalLabels: {
    monthly: 'monatlich',
    quarterly: 'quartalsweise',
    halfAnnually: 'halbjährlich',
    annually: 'jährlich'
  },
  configurator: {
    eyebrow: 'All-inclusive · voll gemanagt',
    heroTitle: 'Webhosting, um das wir uns kümmern.',
    heroSubtitle: 'SSL, Backups auf Knopfdruck, E-Mail und DDoS-Schutz sind im Preis, und wir halten die Plattform aktuell und am Laufen. Hosting, Domains und E-Mail steuerst du aus einem Panel.',
    startNow: 'Jetzt starten',
    entryPrice: 'Pakete ab {price} im Monat, jederzeit mit einem Monat Frist kündbar',
    viewMode: 'Ansicht',
    billing: 'Abrechnung',
    guided: 'Empfehlung',
    allPlans: 'Alle Pläne',
    mostPopular: 'Beliebtestes Paket',
    planTaglines: {
      nano: 'Perfekt für deine erste Website',
      micro: 'Für Blogs und wachsende Projekte',
      mega: 'Für anspruchsvolle Seiten und Shops',
      giga: 'Für E-Commerce und große Projekte',
      tera: 'Für große Portale und Agenturen',
      peta: 'Maximale Leistung für dein Projekt'
    },
    chips: {
      iso27001: 'ISO 27001 Rechenzentrum',
      managedSelfService: 'Managed & Self-Service'
    },
    includedInEveryPlan: 'Inklusive in jedem Paket',
    includedFeatures: {
      sftpCron: 'SFTP-Zugang & Cron-Jobs',
      backupsSsl: 'Backups auf Knopfdruck & SSL-Zertifikate',
      emailWebmail: 'E-Mail-Postfächer & Webmail',
      phpDatabases: 'PHP 8.2 bis 8.5 & MariaDB'
    },
    specsBand: {
      title: 'In allen Paketen enthalten',
      sslIncluded: 'SSL inklusive',
      sslDesc: 'Kostenlose TLS/SSL-Zertifikate',
      uptime: '99% Verfügbarkeit',
      uptimeDesc: 'Garantierte Uptime SLA',
      backups: 'Backups auf Knopfdruck',
      backupsDesc: 'Bis zu 5 je Website, 72 Stunden aufbewahrt',
      emailWebmail: 'E-Mail & Webmail',
      emailWebmailDesc: 'Bis zu 1.000 Postfächer pro Domain',
      php: 'PHP 8.2 – 8.5',
      phpDesc: 'Neueste Version ab Tag eins',
      ddos: 'DDoS-Schutz',
      ddosDesc: 'Enterprise-Level Schutz',
      traffic: 'Unbegrenzter¹ Traffic',
      trafficDesc: 'Keine Datenlimits (Fair Use)',
      monitoring: '24/7 Überwachung',
      monitoringDesc: 'Proaktives Monitoring',
      cancellable: 'Monatlich kündbar',
      cancellableDesc: 'Flexible Vertragslaufzeit',
      fairUseNote: '¹ Unbegrenzt im Rahmen unserer Fair-Use-Richtlinie — normale Nutzung ohne Missbrauch. Bei dauerhaft exzessiver Nutzung kann eine Anpassung nötig sein.'
    },
    storageSelector: 'Speicherbedarf',
    storageOverage: 'Belegst du mehr Speicher, als dein Paket enthält, berechnen wir den Überhang mit 0,05 € netto pro GB und Monat (5,95 Cent inkl. 19 % MwSt.). Es zählen Dateien, Datenbanken und Postfächer aller Websites im Paket.',
    contactSales: {
      title: 'Mehr Speicher?',
      text: 'Für Anforderungen über 2 TB erstellen wir dir ein individuelles Angebot.',
      cta: 'Kontakt aufnehmen'
    },
    allPlansTable: {
      title: 'Alle Hosting-Pakete im Überblick',
      subtitle: 'Vergleiche alle Tarife und finde das passende Paket für dein Projekt.',
      package: 'Paket',
      price: 'Preis',
      storage: 'Speicher',
      support: 'Support',
      backups: 'Backups',
      ssl: 'SSL',
      php: 'PHP',
      choose: 'In den Warenkorb'
    },
    trust: {
      gdpr: 'AVV nach Art. 28 DSGVO',
      uptime: '99% Uptime',
      germanDatacenters: 'Deutsche Rechenzentren'
    }
  },
  productCard: {
    addedToCart: 'Zum Warenkorb hinzugefügt!',
    mainFeatures: 'Hauptmerkmale',
    storage: 'Speicher',
    support: 'Support',
    paymentMethod: 'Zahlungsweise',
    flexibleTerms: 'Flexible Laufzeiten',
    cancellablePeriod: 'Jederzeit kündbar, ein Monat Frist',
    addToCart: 'In den Warenkorb'
  }
} as const

export const comingSoon = {
  badge: 'Demnächst verfügbar',
  dashboardNote: 'Dieses Produkt ist noch nicht bestellbar. Wir informieren dich, sobald es verfügbar ist.'
} as const

export const pricingCalculator = {
  title: 'Preisrechner',
  eyebrow: 'Transparente Preise',
  subtitle: 'Berechne deine monatlichen Kosten für Cloud-Dienste — transparent, ohne versteckte Gebühren.',
  cta: 'Jetzt starten',
  ctaCart: 'In den Warenkorb',
  ctaRegister: 'oder zuerst Konto erstellen',
  domainNote: 'Domain-Preise findest du in unserem {link}.',
  domainCatalogLink: 'TLD-Katalog',
  heroChips: {
    transparent: 'Alle Preise inkl. MwSt.',
    noHiddenFees: 'Keine versteckten Gebühren',
    payg: 'Pay-as-you-go'
  },
  categoriesLabel: 'Produkte auswählen',
  presets: {
    label: 'Schnellstart',
    smallApp: 'Kleine App / API',
    smallAppDesc: '1 vCPU, 2 GiB RAM, 10 GiB Speicher',
    webPlatform: 'Web-Plattform',
    webPlatformDesc: '4 vCPU, 8 GiB RAM, 50 GiB Speicher',
    teamNamespace: 'Team-Namespace',
    teamNamespaceDesc: 'Namespace M + Managed DB',
    custom: 'Individuell',
    customDesc: 'Selbst konfigurieren'
  },
  categories: {
    k8sPayg: 'Kubernetes PAYG',
    s3: 'Object Storage',
    k8sNamespace: 'K8s Namespaces',
    k8sCluster: 'K8s Cluster',
    k8sAddon: 'K8s Addons',
    vserver: 'VServer',
    webhosting: 'Webhosting',
    email: 'E-Mail Hosting'
  },
  k8sPayg: {
    title: 'Kubernetes Pay-as-you-go',
    description: 'Zahle nur, was du nutzt. Ideal für variable Workloads, CI/CD-Pipelines oder Entwicklungsumgebungen.',
    cpuCores: 'CPU-Kerne',
    cpuHint: 'Typisch: 0,5–2 für kleine Apps, 4–8 für Datenbanken',
    cpuHours: 'CPU-Stunden/Monat',
    cpuHoursHint: '730 = 24/7 · Weniger für zeitweise Workloads',
    ramGib: 'RAM (GiB)',
    ramHint: 'Typisch: 1–4 GiB für Webapps, 8–32 GiB für Caches/DBs',
    ramHours: 'RAM-Stunden/Monat',
    ramHoursHint: '730 = 24/7 · Weniger für zeitweise Workloads',
    storageGib: 'Storage (GiB)',
    storageHint: 'Persistente Volumes für Datenbanken, Uploads etc.',
    egressGib: 'Egress (GiB/Monat)',
    egressHint: 'Ausgehender Traffic (API-Responses, Downloads). Intern kostenlos.',
    baseFee: 'Grundgebühr',
    fullMonth: '730 = 24/7'
  },
  s3: {
    title: 'Object Storage',
    description: 'S3-kompatibler Objektspeicher für Backups, Medien und statische Dateien.',
    storageGb: 'Storage (GB)',
    storageHint: 'Gesamter gespeicherter Datenbestand',
    egressGb: 'Egress (GB/Monat)',
    egressHint: 'Downloads und ausgehender Traffic pro Monat',
    baseFee: 'Grundgebühr'
  },
  k8sNamespace: {
    title: 'Kubernetes Namespaces',
    description: 'Geteilter Cluster mit garantierten Ressourcen. Ideal für Teams, die sich nicht um Cluster-Management kümmern wollen.',
    tier: 'Tier',
    quantity: 'Anzahl',
    specs: '{cpu}, {ram}'
  },
  k8sCluster: {
    title: 'Kubernetes Cluster',
    description: 'Dedizierter Cluster mit voller Kontrolle. Für Unternehmen mit besonderen Sicherheits- oder Compliance-Anforderungen.',
    clusterType: 'Cluster-Typ',
    extraWorkers: 'Zusätzliche Worker',
    extraWorkersHint: 'Zusätzliche Worker-Nodes für mehr Kapazität',
    perWorker: 'pro Worker'
  },
  k8sAddon: {
    title: 'Kubernetes Addons',
    description: 'Optionale Zusatzleistungen für deinen Kubernetes-Namespace.',
    quantity: 'Menge'
  },
  vserver: {
    title: 'VServer',
    description: 'Premium-KVM-VPS auf NVMe, Snapshots und Support inklusive. Wähle Größe und Anzahl.',
    tier: 'Größe',
    quantity: 'Anzahl',
    specs: '{cpu} vCPU · {ram} GB RAM · {disk} GB NVMe',
    modePreset: 'Feste Größen (S–XL)',
    modeCustom: 'Frei konfigurieren',
    customDescription: 'Konfiguriere vCPU, RAM und Disk frei. Die Spannen für RAM und Disk skalieren mit deiner vCPU-Wahl.',
    vcpu: 'vCPU',
    ram: 'RAM (GB)',
    disk: 'Disk (GB)',
    boundsHint: 'Bei {vcpu} vCPU: {min}–{max} GB',
    breakdownGross: 'inkl. MwSt.'
  },
  webhosting: {
    title: 'Webhosting',
    description: 'Managed Webhosting mit SSD-Speicher, E-Mail und Backups auf Knopfdruck.',
    tier: 'Paket',
    quantity: 'Anzahl',
    storage: 'Speicher'
  },
  email: {
    title: 'E-Mail Hosting',
    description: 'Bis zu 1.000 Postfächer auf deiner eigenen Domain. Inklusive Speicher und Versand, Überverbrauch wird nach Nutzung abgerechnet.',
    domains: 'Domains',
    domainsHint: '{price} €/Monat pro Domain (brutto)',
    storageGb: 'Speicher gesamt (GB)',
    storageHint: '{included} GB inklusive ({perDomain} GB pro Domain)',
    outbound: 'Ausgehende E-Mails/Monat',
    outboundHint: '{included} inklusive ({perDomain} pro Domain)',
    baseFee: 'Grundgebühr/Domain',
    messages: 'Nachrichten'
  },
  summary: {
    title: 'Zusammenfassung',
    subtotal: 'Zwischensumme',
    total: 'Gesamt',
    perMonth: '/ Monat',
    vatToggle: 'Brutto (inkl. MwSt.)',
    net: 'netto',
    gross: 'brutto',
    vat: 'MwSt. (19%)',
    emptyState: 'Wähle Produkte aus, um eine Schätzung zu erhalten.',
    estimateNote: 'Unverbindliche Schätzung — individuelle Größen konfigurierst du nach der Registrierung im Dashboard.',
    shareLink: 'Link zur Schätzung kopieren',
    linkCopied: 'Link kopiert',
    linkCopiedDesc: 'Teile die Schätzung mit deinem Team.',
    productCount: '{count} Produkt | {count} Produkte',
    emailNeedsDomainTitle: 'Domain für E-Mail wählen',
    emailNeedsDomainMessage: 'E-Mail-Hosting benötigt eine Domain. Bitte wähle auf der E-Mail-Seite eine Domain aus, um sie in den Warenkorb zu legen.'
  }
} as const
